import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import './Auth.css';

import { FloatingField } from '../../components';
import AuthLayout from './AuthLayout';
import { notify } from 'components/Common/Toast';
import { store } from 'redux/store';
import { register as registerUser } from 'redux/slices/auth/authSlice';
import Button from 'components/Common/Button';
import Routes from 'constants/routes';
import { getProblemDetail } from 'api/v1Helpers';
import { readMemberInviteEmail, readMemberInviteToken } from 'utils/memberInviteToken';
import { readStaffInviteEmail, readStaffInviteToken } from 'utils/staffInviteToken';

const formSchema = yup
    .object({
        email: yup.string().required('Email is required').email().lowercase().trim(),
        password: yup.string().required('Password is required').min(10, 'Password must be at least 10 characters'),
        firstName: yup.string().required('First name is required').trim(),
        lastName: yup.string().required('Last name is required').trim(),
    })
    .required();

type RegisterFormInputs = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
};

type RegisterLocationState = {
    email?: string;
    lockEmail?: boolean;
} | null;

function Register() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<typeof store.dispatch>();
    const [isLoading, setIsLoading] = useState(false);

    const locState = location.state as RegisterLocationState;
    const invitedEmail = (locState?.email || readStaffInviteEmail() || readMemberInviteEmail() || '').toLowerCase().trim();
    const emailLocked = Boolean(invitedEmail && (locState?.lockEmail || readStaffInviteEmail() || readMemberInviteEmail()));

    useEffect(() => {
        // Portal registration is invite-driven only (staff or parishioner invite).
        if (!invitedEmail && !readStaffInviteToken() && !readMemberInviteToken()) {
            navigate(Routes.auth.login, { replace: true });
        }
    }, [invitedEmail, navigate]);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<RegisterFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            email: invitedEmail,
            password: '',
            firstName: '',
            lastName: '',
        },
    });

    const submit = async (data: RegisterFormInputs) => {
        // Prefer the invited email when locked (UX only — accept still enforces match on the server).
        const email = emailLocked ? invitedEmail : data.email;
        if (emailLocked && data.email.toLowerCase().trim() !== invitedEmail) {
            notify('Use the email address this invitation was sent to.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const result = (await dispatch(
                registerUser({
                    email,
                    password: data.password,
                    firstName: data.firstName,
                    lastName: data.lastName,
                })
            )) as { error?: unknown } | undefined;
            if (result?.error) return;
            notify('Account created. Enter the verification code we emailed you.', 'success');
            navigate(Routes.auth.verifyEmail, { state: { email } });
        } catch (error: unknown) {
            notify(getProblemDetail(error) || 'Registration failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout>
            <form onSubmit={handleSubmit(submit)} className="Auth-form">
                <p className="Auth-form-title">Create an account</p>
                {emailLocked ? (
                    <p className="Auth-form-subtitle" style={{ marginBottom: 8, fontSize: 14, opacity: 0.85 }}>
                        This invitation is for {invitedEmail}. Email cannot be changed.
                    </p>
                ) : null}
                <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField label="First name" value={value} onChange={onChange} error={errors.firstName?.message} />
                    )}
                />
                <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField label="Last name" value={value} onChange={onChange} error={errors.lastName?.message} />
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Email"
                            value={emailLocked ? invitedEmail : value}
                            onChange={emailLocked ? () => undefined : onChange}
                            error={errors.email?.message}
                            disabled={emailLocked}
                            readOnly={emailLocked}
                        />
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            error={errors.password?.message}
                        />
                    )}
                />
                <Button type="submit" disabled={isLoading} loading={isLoading} title="CREATE ACCOUNT" />
            </form>
        </AuthLayout>
    );
}

export default Register;
